<template>
  <div class="auth-page">
    <div class="auth-card">
      <AuthPageLogo class="auth-card__logo" />
      <h1 class="auth-card__title">{{ titleByMode }}</h1>
      <p class="auth-card__subtitle">
        {{ subtitleByMode }}
      </p>

      <!-- Обычная форма входа -->
      <template v-if="authMode === 'login'">
        <form class="auth-form" @submit.prevent="handleLoginSubmit">
          <label class="auth-form__label" for="login">Логин</label>
          <input
            id="login"
            v-model.trim="form.login"
            class="auth-form__input"
            type="text"
            autocomplete="username"
            placeholder="Введите логин"
            :disabled="isSubmitting"
          />

          <label class="auth-form__label" for="password">Пароль</label>
          <input
            id="password"
            v-model="form.password"
            class="auth-form__input"
            type="password"
            autocomplete="current-password"
            placeholder="Введите пароль"
            :disabled="isSubmitting"
          />

          <p v-if="errorMessage" class="auth-form__error">
            <span>{{ errorMessage }}</span>
          </p>

          <BaseButton
            text="Войти"
            styles="primary-gradient"
            full-width
            :is-disabled="isLoginSubmitDisabled"
            vibration="critical"
            @on-submit="handleLoginSubmit"
          />
        </form>

        <p v-if="!isTelegramApp" class="auth-card__link">
          <button
            type="button"
            class="auth-card__link-button"
            @click="switchToRestoreStep1"
          >
            Забыли пароль?
          </button>
        </p>
        <p class="auth-card__hint">
          Нет логина? Откройте приложение в Telegram Web App и установите
          учетные данные в разделе &laquo;Настройки входа&raquo;.
        </p>
      </template>

      <!-- Восстановление пароля: шаг 1 — identifier -->
      <template v-else-if="authMode === 'restore-step1'">
        <form class="auth-form" @submit.prevent="handleRestoreStep1">
          <label class="auth-form__label" for="restore-identifier"
            >Логин или email</label
          >
          <input
            id="restore-identifier"
            v-model.trim="restore.identifier"
            class="auth-form__input"
            type="text"
            autocomplete="username"
            placeholder="Введите логин или email"
            :disabled="isSubmitting"
          />
          <p
            v-if="errorMessage"
            class="auth-form__error"
            :class="{ 'auth-form__error--warning': showRestoreTelegramCta }"
          >
            {{ errorMessage }}
          </p>
          <BaseButton
            v-if="showRestoreTelegramCta"
            text="Перейти в Telegram"
            styles="primary-gradient"
            full-width
            :is-disabled="isSubmitting"
            vibration="success"
            @on-submit="openTelegramRegisterWebApp"
          />
          <BaseButton
            v-else
            text="Отправить код"
            styles="primary-gradient"
            full-width
            :is-disabled="!restore.identifier.trim() || isSubmitting"
            vibration="critical"
            @on-submit="handleRestoreStep1"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Восстановление пароля: шаг 2 — код из email -->
      <template v-else-if="authMode === 'restore-step2'">
        <form class="auth-form" @submit.prevent="handleRestoreStep2">
          <p v-if="restore.maskedEmail" class="auth-card__masked-email">
            Код отправлен на {{ restore.maskedEmail }}
          </p>
          <label class="auth-form__label" for="restore-code"
            >Код из письма</label
          >
          <input
            id="restore-code"
            v-model.trim="restore.code"
            class="auth-form__input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="6 цифр"
            maxlength="6"
            :disabled="isSubmitting"
          />
          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <BaseButton
            text="Проверить код"
            styles="primary-gradient"
            full-width
            :is-disabled="isRestoreStep2Disabled"
            vibration="critical"
            @on-submit="handleRestoreStep2"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Восстановление пароля: шаг 3 — новый пароль -->
      <template v-else-if="authMode === 'restore-step3'">
        <form class="auth-form" @submit.prevent="handleRestoreStep3">
          <label class="auth-form__label" for="restore-new-password"
            >Новый пароль</label
          >
          <input
            id="restore-new-password"
            v-model="restore.newPassword"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="От 8 до 32 символов"
            :disabled="isSubmitting"
          />
          <label class="auth-form__label" for="restore-confirm-password"
            >Повторите пароль</label
          >
          <input
            id="restore-confirm-password"
            v-model="restore.confirmPassword"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="Повторите новый пароль"
            :disabled="isSubmitting"
          />
          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <BaseButton
            text="Сохранить пароль"
            styles="primary-gradient"
            full-width
            :is-disabled="isRestoreStep3Disabled"
            vibration="critical"
            @on-submit="handleRestoreStep3"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted } from "vue";
import { useAuth } from "@/shared/lib/auth/useAuth";
import BaseButton from "@/ui/components/Base/BaseButton.vue";
import AuthPageLogo from "@/assets/icons/auth-page-logo.svg";
import { SentryService } from "@/shared/lib/sentry/sentryService";
import { TELEGRAM_REGISTER_WEBAPP_URL } from "@/shared/config/browserLinks";

const emit = defineEmits<{
  (e: "login-success"): void;
}>();

type AuthMode = "login" | "restore-step1" | "restore-step2" | "restore-step3";

const {
  loginWithCredentials,
  restorePassword,
  verifyRestoreCode,
  setRestoredPassword,
} = useAuth();

const form = reactive({
  login: "",
  password: "",
});

const authMode = ref<AuthMode>("login");
const isSubmitting = ref(false);
const errorMessage = ref("");
const showRestoreTelegramCta = ref(false);

const restore = reactive({
  identifier: "",
  code: "",
  resetSecret: "",
  maskedEmail: "",
  newPassword: "",
  confirmPassword: "",
});

const isTelegramApp = computed(() => {
  const tg = (window as any).Telegram?.WebApp;
  if (!tg) return false;
  const initData = tg.initData;
  return Boolean(initData && initData.length > 0);
});

const titleByMode = computed(() => {
  if (authMode.value === "login") return "Вход в аккаунт";
  if (authMode.value === "restore-step1") return "Восстановление пароля";
  if (authMode.value === "restore-step2") return "Код из письма";
  if (authMode.value === "restore-step3") return "Новый пароль";
  return "Вход в аккаунт";
});

const subtitleByMode = computed(() => {
  if (authMode.value === "login") {
    return "Используйте логин и пароль, которые вы установили в Telegram Web App.";
  }
  if (authMode.value === "restore-step1") {
    return "Введите логин или email — мы отправим код на почту.";
  }
  if (authMode.value === "restore-step2") {
    return "Введите 6-значный код из письма.";
  }
  if (authMode.value === "restore-step3") {
    return "Придумайте новый пароль от 8 до 32 символов.";
  }
  return "";
});

const isLoginSubmitDisabled = computed(() => {
  return !form.login || !form.password || isSubmitting.value;
});

const isRestoreStep2Disabled = computed(() => {
  return restore.code.length !== 6 || isSubmitting.value;
});

const isRestoreStep3Disabled = computed(() => {
  const p = restore.newPassword;
  const c = restore.confirmPassword;
  const validLength = p.length >= 8 && p.length <= 32;
  return !validLength || p !== c || isSubmitting.value;
});

function normalizeApiError(error: unknown): string {
  const fallback = "Не удалось выполнить действие. Попробуйте ещё раз.";
  if (error && typeof error === "object" && "data" in error) {
    const d = (error as { data?: any }).data;
    if (d?.message && typeof d.message === "string") return d.message;
    if (typeof d?.detail === "string" && d.detail) return d.detail;
    if (Array.isArray(d?.detail) && d.detail.length) {
      const msgs = d.detail.map((x: { msg?: string }) => x.msg).filter(Boolean);
      if (msgs.length) return msgs.join(", ");
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function isMissingEmailForRestoreError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { status?: number; data?: any };
  if (e.status !== 400 || !e.data) return false;
  const detail = e.data.detail;
  if (typeof detail === "string") {
    return detail === "У пользователя не указан email в профиле";
  }
  return false;
}

function openTelegramRegisterWebApp() {
  const url = TELEGRAM_REGISTER_WEBAPP_URL;
  const tg = (window as any).Telegram?.WebApp;
  if (tg && typeof tg.openLink === "function") {
    tg.openLink(url);
  } else {
    window.open(url, "_blank");
  }
}

function clearRestoreState() {
  restore.identifier = "";
  restore.code = "";
  restore.resetSecret = "";
  restore.maskedEmail = "";
  restore.newPassword = "";
  restore.confirmPassword = "";
  errorMessage.value = "";
  showRestoreTelegramCta.value = false;
}

function backToLogin() {
  SentryService.trackButtonClick("Назад ко входу (восстановление пароля)", {
    from_step: authMode.value,
  });
  clearRestoreState();
  authMode.value = "login";
}

function switchToRestoreStep1() {
  if (isTelegramApp.value) return;
  SentryService.trackButtonClick("Забыли пароль?", {
    flow: "password_restore",
  });
  clearRestoreState();
  errorMessage.value = "";
  authMode.value = "restore-step1";
}

const handleLoginSubmit = async () => {
  if (isLoginSubmitDisabled.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  try {
    await loginWithCredentials({
      login: form.login,
      password: form.password,
    });
    emit("login-success");
  } catch (error) {
    errorMessage.value = normalizeApiError(error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep1 = async () => {
  if (!restore.identifier.trim() || isSubmitting.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  showRestoreTelegramCta.value = false;
  try {
    const res = await restorePassword({
      identifier: restore.identifier.trim(),
    });
    if (res.success) {
      restore.maskedEmail = res.masked_email ?? "";
      authMode.value = "restore-step2";
      SentryService.trackUserAction("Восстановление пароля: код отправлен", {
        step: 1,
        has_masked_email: !!res.masked_email,
      });
    } else {
      errorMessage.value = res.message || "Не удалось отправить код.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 1 },
      "password_restore_step_1",
    );
    if (isMissingEmailForRestoreError(error)) {
      errorMessage.value =
        "У вас не заполнены необходимые данные для восстановления пароля. \nКликните на кнопку ниже, чтобы заполнить их";
      showRestoreTelegramCta.value = true;
    } else {
      errorMessage.value = normalizeApiError(error);
      showRestoreTelegramCta.value = false;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep2 = async () => {
  if (restore.code.length !== 6 || isSubmitting.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  try {
    const res = await verifyRestoreCode({
      identifier: restore.identifier.trim(),
      code: restore.code,
    });
    if (res.success && res.reset_secret) {
      restore.resetSecret = res.reset_secret;
      authMode.value = "restore-step3";
      SentryService.trackUserAction("Восстановление пароля: код проверен", {
        step: 2,
      });
    } else {
      errorMessage.value =
        res.message || "Неверный или истёкший код. Запросите новый.";
      restore.code = "";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 2 },
      "password_restore_step_2",
    );
    errorMessage.value = normalizeApiError(error);
    restore.code = "";
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep3 = async () => {
  const p = restore.newPassword;
  if (
    p.length < 8 ||
    p.length > 32 ||
    p !== restore.confirmPassword ||
    isSubmitting.value
  )
    return;
  if (!restore.resetSecret) {
    errorMessage.value = "Сессия восстановления истекла. Начните заново.";
    backToLogin();
    return;
  }
  isSubmitting.value = true;
  errorMessage.value = "";
  try {
    const res = await setRestoredPassword({
      identifier: restore.identifier.trim(),
      reset_secret: restore.resetSecret,
      new_password: p,
    });
    if (res.success) {
      try {
        await loginWithCredentials({
          login: restore.identifier.trim(),
          password: p,
        });
        SentryService.trackUserAction(
          "Восстановление пароля: пароль изменён, автологин",
          { step: 3, autologin: true },
        );
        clearRestoreState();
        emit("login-success");
      } catch (loginErr) {
        SentryService.trackUserAction(
          "Восстановление пароля: пароль изменён, автологин не выполнен",
          { step: 3, autologin: false },
        );
        errorMessage.value = "Пароль изменён. Войдите с новым паролем.";
        clearRestoreState();
        authMode.value = "login";
      }
    } else {
      errorMessage.value = res.message || "Не удалось сохранить пароль.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 3 },
      "password_restore_step_3",
    );
    errorMessage.value = normalizeApiError(error);
    backToLogin();
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  if (authMode.value !== "login") clearRestoreState();
});
</script>

<style lang="scss" scoped>
.auth-page {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.auth-card {
  max-width: 360px;
  width: 100%;
  background: rgba(10, 12, 15, 0.75);
  border-radius: 20px;
  padding: 32px 24px;
  box-sizing: border-box;
  backdrop-filter: blur(8px);
  text-align: center;
  color: $neutral-100;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__logo {
    width: 120px;
    height: auto;
    margin: 0 auto 8px;
  }

  &__title {
    @include h1;
    margin: 0;
  }

  &__subtitle {
    @include p2-regular;
    margin: 0;
    color: $neutral-60;
  }

  &__hint {
    margin: 8px 0 0;
    @include p3-regular;
    font-size: 10px;
    color: $neutral-60;
  }

  &__link {
    margin: 0;
  }

  &__link-button {
    background: none;
    border: none;
    padding: 0;
    color: $primary-100;
    @include p1-medium;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      opacity: 0.9;
    }
  }

  &__back {
    background: none;
    border: none;
    padding: 8px 0;
    color: $neutral-60;
    @include p2-medium;
    cursor: pointer;

    &:hover {
      color: $neutral-100;
    }
  }

  &__masked-email {
    @include p3-regular;
    color: $neutral-60;
    margin: 0 0 4px;
    text-align: left;
  }
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;

  &__label {
    @include p2-medium;
    color: $neutral-100;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    border-radius: 12px;
    border: 2px solid $secondary-200;
    background: $secondary-200;
    color: $neutral-100;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $primary-100;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &::placeholder {
      color: $neutral-60;
    }
  }

  &__error {
    @include p3-regular;
    color: #ff4d4f;
    margin: 4px 0 0;
  }

  &__error--warning {
    color: #ffa940;
    font-size: 12px;
    white-space: break-spaces;
  }

  :deep(.button-base-block) {
    width: 100%;
  }

  :deep(.button-base.full-width) {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    box-sizing: border-box;
  }
}
</style>
